import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  class: "d-flex",
  style: {"min-height":"45vw"}
}
const _hoisted_2 = { class: "content p-4 w-100" }
const _hoisted_3 = { class: "card-title mt-0" }
const _hoisted_4 = { class: "row mr-3 v-theme--light" }
const _hoisted_5 = { class: "mb-4" }
const _hoisted_6 = { class: "form-footer p-2" }

import OrganizationAutoComplete from "@/components/AccountParts/OrganizationAutoComplete.vue";
import NotificationSettings from '@/components/AccountParts/NotificationSettings.vue';
import { generatePassword, generateEmail, scrollToTop } from "@/services/functions";
import AccountNotificationSettingsType from '@/types/AccountNotificationSettings';
import DepartmentColumn from "@/components/AccountParts/DepartmentColumn.vue";
import AccountTextRow from "@/components/AccountParts/AccountTextRow.vue";
import PasswordForm from "@/components/AccountParts/PasswordForm.vue";
import TextFields from "@/components/AccountParts/TextFieldsRow.vue";
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi";
import { ref, Ref, onMounted, onBeforeUnmount, watch } from "vue";
import TextArea from "@/components/AccountParts/TextAreaRow.vue";
import ToggleRow from "@/components/AccountParts/ToggleRow.vue";
import InfoModal from '@/components/AccountParts/InfoModal.vue';
import { scrollToErrorComponent } from '@/services/validators';
import { initialAccount } from "@/services/functions/account";
import Select from "@/components/AccountParts/SelectRow.vue";
import { useAuth } from "@/services/user/Auth0UserProvider";
import { routingAuthorityNames } from '@/services/consts';
import getOfficesType from "@/types/responses/getOffices";
import FormRow from "@/components/Common/FormRow.vue";
import Api from "@/services/api/ApiServiceFabrick";
import SelfAccountType from "@/types/SelfAccount";
import { useRoute, useRouter } from "vue-router";
import "gitart-vue-dialog/dist/style.css";
import accountType from "@/types/account";
import {
  MANAGER_ROLE_ID,
  DRIVER_ROLE_ID,
  MANAGEMENT_OFFICE,
  SUB_MANAGEMENT_OFFICE,
  INSTALLATION_OFFICE,
  OPERATION_OFFICE,
  DEPARTMENT,
  DEFAULT_SELECTED_OFFICE,
  BLANK_ROLE,
  ROLES,
  INVALID_ACCOUNT_PROPS,
  BLANK_OFFICEABLE_TYPE,
} from "@/services/consts/account";
import i18n from "@/lang/i18n";

type officeType = {
  id: number | null
  type: string
  name: string | undefined
  departments: getOfficesType['departments']
}

type officeableType = {
  id: number | null
  name: string | null
  type: string | null
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AccountForm',
  props: {
    showFlash: { type: Function },
    switchContentLoading: { type: Function }
  },
  setup(__props: any) {

const props = __props

const router = useRouter();
const route = useRoute();
const accountId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id ?? null
const account: Ref<accountType> = ref(initialAccount())
const errors: Ref<{[key: string]: string[]}> = ref({})
const officeables: Ref<officeType[]> = ref([])
const beforeEmail: Ref<string> = ref("")
const modalState: Ref<boolean> = ref(false)
const isCreatingOrUpdating: Ref<boolean> = ref(false)
const selectedDepartment: Ref<{
  id: number | null,
  operation_office_id: number | null,
  name: string | null
}> = ref({ id: null, operation_office_id: null, name: null })
const selectedRole: Ref<{ name: string, value: number | null }> = ref(BLANK_ROLE)
const initialDepartmentId: Ref<number | null> = ref(null)
const createAndUpdateAuthority: Ref<boolean> = ref(false)
const selectedOfficeableType: Ref<officeableType> = ref(BLANK_OFFICEABLE_TYPE)
const selectedOffice: Ref<officeType> = ref(DEFAULT_SELECTED_OFFICE)
const selfAccountOfficeableType: Ref<string> = ref("")
const showableOfficeableTypes: Ref<any[]> = ref([])
const disableWatchOfficeableType: Ref<boolean> = ref(false)
const disableWatchRole: Ref<boolean> = ref(false)
const roleAssignAuthority: Ref<boolean> = ref(false)
const temperatureAuthority: Ref<boolean> = ref(false)
const outputDataAuthority: Ref<boolean> = ref(false)
const notificationSettings: Ref<AccountNotificationSettingsType> = ref({
  riskyDriving: {
    rapid_acceleration: true,
    rapid_deceleration: true,
    steep_turn: true,
  },
  cvrUpload: {
    completed: true,
  },
  temperature: {
    sensor_disabled: true,
    low_battery: true,
  },
  outputData: {
    completed: true,
  },
})
const { loginWithRedirect } = useAuth();

onMounted( async() => {
  props.switchContentLoading(true);
  scrollToTop()

  await getAuthorities()
  checkCreateAuthority()

  await Promise.all([
    getSelfAccount(),
    getOffices(),
  ]).finally(() => {
    if (!accountId) {
      setNotificationSettings(account.value.account_notification_setting)
      return props.switchContentLoading(false);
    }
  })

  if (accountId) {
    await getAccount(Number(accountId));
  }
})

function title() {
  return accountId ? i18n.global.t('pages.accounts.title.edit') : i18n.global.t('pages.accounts.title.create');
}

function isManagementOffice() {
  return selfAccountOfficeableType.value == MANAGEMENT_OFFICE.type
}

function isSubManagementOffice() {
  return selfAccountOfficeableType.value == SUB_MANAGEMENT_OFFICE.type
}

function isOperationOffice() {
  return selfAccountOfficeableType.value == OPERATION_OFFICE.type
}

function isSelectedManagementOffice() {
  return selectedOfficeableType.value.type == MANAGEMENT_OFFICE.type
}
function isSelectedSubManagementOffice() {
  return selectedOfficeableType.value.type == SUB_MANAGEMENT_OFFICE.type
}
function isSelectedOperationOffice() {
  return selectedOfficeableType.value.type == OPERATION_OFFICE.type
}

function isSelectedManager() {
  return !!(
    (isSelectedOperationOffice() && selectedRole.value && selectedRole.value['value'] == MANAGER_ROLE_ID) ||
    isSelectedManagementOffice() ||
    isSelectedSubManagementOffice()
  )
}

function isSelectedDriver() {
  return !!(isSelectedOperationOffice() && selectedRole.value && selectedRole.value['value'] == DRIVER_ROLE_ID)
}

function isDriverMode() {
  return isSelectedDriver() && account.value.enabled_driver_mode
}

async function getAuthorities() {
  const authorityList = await AuthorityCacheApi.getWithParse();
  if (accountId) {
    createAndUpdateAuthority.value = !!authorityList["octlink.user.update"];
  } else {
    createAndUpdateAuthority.value = !!authorityList["octlink.user.create"];
  }
  roleAssignAuthority.value = !!authorityList["octlink.role_attach.update"];
  temperatureAuthority.value = !!authorityList[routingAuthorityNames.Temperature];
  outputDataAuthority.value = !!authorityList[routingAuthorityNames.DownloadData];
}

function checkCreateAuthority() {
  if (!accountId && !createAndUpdateAuthority.value) {
    props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", false);
    router.push("/accounts");
    return;
  }
}

async function getSelfAccount() {
  await Api().getSelfAccount().then((data: SelfAccountType) => {
    selfAccountOfficeableType.value = data.officeable_type.split("\\")[2]
    const roleId = data.role_id;
    if (roleId == DRIVER_ROLE_ID) {
      return driverAcction();
    }

    setShowableOfficeableType()
  })
}

function driverAcction() {
  props.switchContentLoading(false);
  props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", false);
  router.push("/accounts");
}

async function getOffices() {
  await Api().getOffices().then((data: getOfficesType[]) => {
    officeables.value = data.map((office) => {
      return {
        id: office.id,
        type: office.type.split("\\")[2],
        name: office.company_name ?? office.operation_office_name,
        departments: office.departments ?? [],
      }
    });
  });
}

async function getAccount(accountId: number) {
  disableWatchOfficeableType.value = true;
  disableWatchRole.value = true;

  Api().getAccount(accountId).then((data: accountType) => {
    account.value = data;
    beforeEmail.value = data.email
    account.value.no_email = data.temporary_email?.length ? true : false;
    initialDepartmentId.value = data.department_id

    setSelectedOfficeableType(data);
    setSelectedRole(data)
    if (
      (data.officeable_type.split("\\")[2] == OPERATION_OFFICE.type && data.role_id == MANAGER_ROLE_ID) ||
      (data.officeable_type.split("\\")[2] == MANAGEMENT_OFFICE.type) ||
      (data.officeable_type.split("\\")[2] == SUB_MANAGEMENT_OFFICE.type)
    ) {
      setNotificationSettings(data.account_notification_setting)
    } else {
      setNotificationSettings(initialAccount().account_notification_setting)
    }
    resetOffices(selectedOfficeableType.value, data.officeable_id);
  }).catch((error) => {
    const res = error.response;
    if (res.status == 403) {
      props.showFlash(i18n.global.t('pages.common.messages.notEditAuthority'), "error", true);
      router.push("/accounts/");
    }
  }).finally(() => {
    disableWatchRole.value = false;
    props.switchContentLoading(false);
  });
}

function setSelectedOfficeableType(data: accountType) {
  switch (data.officeable_type.split("\\")[2]) {
    case MANAGEMENT_OFFICE.type:
      return selectedOfficeableType.value = MANAGEMENT_OFFICE;
    case SUB_MANAGEMENT_OFFICE.type:
      return selectedOfficeableType.value = SUB_MANAGEMENT_OFFICE;
    case OPERATION_OFFICE.type:
      return selectedOfficeableType.value = OPERATION_OFFICE;
    default:
      return selectedOfficeableType.value = INSTALLATION_OFFICE;
  }
}

function setSelectedRole(data: accountType) {
  if (data.officeable_type.split("\\")[2] == OPERATION_OFFICE.type) {
    selectedRole.value = ROLES.find((role) => {
      return role.value == data.role_id
    }) ?? BLANK_ROLE;
  }
}

function setNotificationSettings(data: accountType['account_notification_setting']) {
  const riskyDriving = data.risky_driving_alerts;
  const cvrUpload = data.cvr_upload_alerts;
  const temperature = data.temperature_alerts;
  const outputData = data.output_data_alerts;

  notificationSettings.value = {
    riskyDriving: {
      rapid_acceleration: riskyDriving.rapid_acceleration,
      rapid_deceleration: riskyDriving.rapid_deceleration,
      steep_turn: riskyDriving.steep_turn,
    },
    cvrUpload: {
      completed: cvrUpload.completed,
    },
    temperature: {
      sensor_disabled: temperature.sensor_disabled,
      low_battery: temperature.low_battery,
    },
    outputData: {
      completed: outputData.completed,
    },
  }
}

function setShowableOfficeableType() {
  switch (selfAccountOfficeableType.value) {
    case MANAGEMENT_OFFICE.type:
      showableOfficeableTypes.value = [
        MANAGEMENT_OFFICE,
        SUB_MANAGEMENT_OFFICE,
        INSTALLATION_OFFICE,
        OPERATION_OFFICE,
      ];
      break;
    case SUB_MANAGEMENT_OFFICE.type:
      showableOfficeableTypes.value = [
        SUB_MANAGEMENT_OFFICE,
        INSTALLATION_OFFICE,
        OPERATION_OFFICE,
      ];
      break;
    case OPERATION_OFFICE.type:
    case DEPARTMENT.type:
      showableOfficeableTypes.value = [OPERATION_OFFICE];
      break;
    default:
      showableOfficeableTypes.value = [];
  }

  if (showableOfficeableTypes.value.length) {
    showableOfficeableTypes.value.unshift(BLANK_OFFICEABLE_TYPE);
  }
}

function resetOffices(officeableType: officeableType, officeableId: number | null = null) {
  if (!officeableType.type || !officeableId) {
    return selectedOffice.value = DEFAULT_SELECTED_OFFICE;
  }

  let filteredOffices: officeType[] = []
  filteredOffices = officeables.value.filter((office) => {
    return office.type == officeableType.type
  });

  selectedOffice.value = filteredOffices.find((office: officeType) => {
    return office.id == officeableId
  }) ?? DEFAULT_SELECTED_OFFICE
}

function makeMailAdress() {
  if (!account.value.email && selectedOffice.value?.id) {
    account.value.email = generateEmail(account.value.driver_id, selectedOffice.value.id)
  }
}

function checkEmailAndPassword(bool: boolean) {
  if (!isDriverMode()) {
    return false
  }

  if (!bool) {
    return bool
  }

  const diffEmail = beforeEmail.value != account.value.email
  const existsName = !!account.value.last_name && !!account.value.first_name
  const existsOfficeAndDriverId = !!account.value.driver_id && !!selectedOffice.value?.id
  const isNecessaryPassword = passwordConditions()

  return (diffEmail || !account.value.email || isNecessaryPassword)
    && existsName
    && existsOfficeAndDriverId
}

function openAnnounceRoleAssignModal() {
  makeMailAdress()
  makePassword()
  modalState.value = true
}

function makePassword() {
  if (!accountId && !account.value.password) {
    account.value.password = account.value.driver_id ? generatePassword(account.value.driver_id) : ""
  }
}

function passwordConditions() {
  if (accountId) {
    return !!account.value.password
  }
  return !account.value.password && account.value.driver_id
}

function createOrUpdate(bool = true) {
  errors.value = {}
  if (accountId && checkEmailAndPassword(bool)) {
    return openAnnounceRoleAssignModal();
  }

  isCreatingOrUpdating.value = true;
  const apiRequetFunc = accountId ? Api().updateAccount : Api().createAccount;

  apiRequetFunc(makeParams()).then((data) => {
    if (!accountId) {
      return openAnnounceRoleAssignModal()
    }
    props.showFlash(i18n.global.t('pages.common.messages.success'), "success", true);
    router.push("/accounts");
  }).catch((error) => {
    const res = error.response;
    switch (res.status) {
      case 401:
        loginWithRedirect({
          appState: { targetUrl: location.pathname },
        });
        break;
      case 403:
        location.reload();
        break;
      case 422:
        errors.value = res.data.errors;
        scrollToErrorComponent();
    }
  }).finally(() => {
    isCreatingOrUpdating.value = false;
  });
}

function makeParams() {
  const params = JSON.parse(JSON.stringify(account.value))
  params["no_email"] = params["no_email"] ? 1 : 0;
  params["officeable_type"] = selectedOfficeableType.value.id;
  params["officeable_id"] = selectedOffice.value?.id;
  params['role_id'] = makeRoleId()
  if (params["role_id"] != DRIVER_ROLE_ID) {
    params["driver_id"] = null;
    params["enabled_driver_mode"] = false
  }
  if (isDriverMode()) {
    params["no_email"] = 0;
  }

  params["department_id"] = selectedDepartment.value?.id ?? null;

  INVALID_ACCOUNT_PROPS.forEach((prop: string) => {
    delete params[prop]
  });

  if (!params["enabled_driver_mode"]) {
    delete params["password"];
  }

  if (isSelectedManager()) {
    params["account_notification_setting"] = makeAccountNotificationSettingParams();
  }

  return params;
}

function makeAccountNotificationSettingParams() {
  let temperatureAlerts = {
    sensor_disabled: false,
    low_battery: false,
  }
  if (temperatureAuthority.value) {
    temperatureAlerts = notificationSettings.value.temperature
  }

  let outputDataAlerts = {
    completed: false,
  }
  if (outputDataAuthority.value) {
    outputDataAlerts = notificationSettings.value.outputData
  }

  return {
    risky_driving_alerts: notificationSettings.value.riskyDriving,
    cvr_upload_alerts: notificationSettings.value.cvrUpload,
    temperature_alerts: temperatureAlerts,
    output_data_alerts: outputDataAlerts,
  }
}

function makeRoleId() {
  return isSelectedOperationOffice() ? selectedRole.value['value'] : MANAGER_ROLE_ID;
}

function isEmailRequired() {
  return isOperationOffice() && selectedRole.value['value'] == MANAGER_ROLE_ID
}

watch(() => selectedOfficeableType.value, (value) => {
  if (value.type != OPERATION_OFFICE.type) {
    selectedRole.value = BLANK_ROLE;
  }
  if (disableWatchOfficeableType.value) {
    return disableWatchOfficeableType.value = false;
  }
  resetOffices(value);
})

watch(() => account.value.enabled_driver_mode, (value) => {
  if (value && account.value.driver_id && selectedOffice.value?.id) {
    makeMailAdress()
  }
})

watch(() => selectedRole.value, (value) => {
  if (value && !disableWatchRole.value) {
    account.value.enabled_driver_mode = true
  }
})

onBeforeUnmount(() => {
  props.switchContentLoading(false);
})

return (_ctx: any,_cache: any) => {
  const _component_v_radio = _resolveComponent("v-radio")!
  const _component_v_radio_group = _resolveComponent("v-radio-group")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h4", _hoisted_3, _toDisplayString(title()), 1),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(Select, {
            title: _unref(i18n).global.t('pages.accounts.accountCategory'),
            required: true,
            modelValue: selectedOfficeableType.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedOfficeableType).value = $event)),
            itemTitle: "name",
            itemValue: "id",
            disabled: isDriverMode(),
            items: showableOfficeableTypes.value,
            errors: errors.value['officeable_type']
          }, null, 8, ["title", "modelValue", "disabled", "items", "errors"]),
          _createVNode(FormRow, {
            title: _unref(i18n).global.t('pages.accounts.officeableName'),
            required: true
          }, {
            default: _withCtx(() => [
              _createVNode(OrganizationAutoComplete, {
                label: "",
                modelValue: selectedOffice.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedOffice).value = $event)),
                selectedOfficeableType: selectedOfficeableType.value,
                clearable: false,
                errorMessages: errors.value['officeable_id'] ?? []
              }, null, 8, ["modelValue", "selectedOfficeableType", "errorMessages"])
            ]),
            _: 1
          }, 8, ["title"]),
          (isSelectedOperationOffice())
            ? (_openBlock(), _createBlock(FormRow, {
                key: 0,
                title: _unref(i18n).global.t('pages.accounts.role'),
                required: true
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_radio_group, {
                    modelValue: selectedRole.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedRole).value = $event)),
                    "error-messages": errors.value['role_id'],
                    inline: "",
                    class: "role-radio-group pt-2",
                    color: "primary"
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(ROLES), (role) => {
                        return (_openBlock(), _createBlock(_component_v_radio, {
                          key: role.value,
                          label: role.name,
                          value: role
                        }, null, 8, ["label", "value"]))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["modelValue", "error-messages"])
                ]),
                _: 1
              }, 8, ["title"]))
            : _createCommentVNode("", true),
          _createVNode(TextFields, {
            modelValue: account.value,
            title: _unref(i18n).global.t('pages.accounts.form.name'),
            labels: [
              _unref(i18n).global.t('pages.accounts.form.lastName'),
              _unref(i18n).global.t('pages.accounts.form.firstName')
            ],
            errors: errors.value,
            itemKeys: ['last_name', 'first_name'],
            required: true
          }, null, 8, ["modelValue", "title", "labels", "errors"]),
          _createVNode(TextFields, {
            title: _unref(i18n).global.t('pages.accounts.form.nameKana'),
            labels: [
              _unref(i18n).global.t('pages.accounts.form.lastNameKana'),
              _unref(i18n).global.t('pages.accounts.form.firstNameKana')
            ],
            errors: errors.value,
            modelValue: account.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((account).value = $event)),
            itemKeys: ['last_name_kana', 'first_name_kana'],
            required: true
          }, null, 8, ["title", "labels", "errors", "modelValue"]),
          (isSelectedDriver())
            ? (_openBlock(), _createBlock(AccountTextRow, {
                key: 1,
                title: _unref(i18n).global.t('pages.accounts.driverId'),
                errors: errors.value['driver_id'] ?? [],
                modelValue: account.value.driver_id,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((account.value.driver_id) = $event)),
                required: true
              }, null, 8, ["title", "errors", "modelValue"]))
            : _createCommentVNode("", true),
          (isSelectedDriver())
            ? (_openBlock(), _createBlock(ToggleRow, {
                key: 2,
                title: _unref(i18n).global.t('pages.accounts.form.driverMode'),
                modelValue: account.value.enabled_driver_mode,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((account.value.enabled_driver_mode) = $event))
              }, null, 8, ["title", "modelValue"]))
            : _createCommentVNode("", true),
          (!account.value.no_email || isSelectedDriver())
            ? (_openBlock(), _createBlock(AccountTextRow, {
                key: 3,
                title: _unref(i18n).global.t('pages.accounts.form.emailAddress'),
                errors: errors.value['email'] ?? [],
                modelValue: account.value.email,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((account.value.email) = $event)),
                generateButtonName: _unref(i18n).global.t('pages.accounts.form.automaticIssuanceEmailAddress'),
                isGenerate: isDriverMode(),
                driverId: account.value.driver_id,
                officeId: selectedOffice.value?.id ? selectedOffice.value.id : null,
                required: isEmailRequired()
              }, null, 8, ["title", "errors", "modelValue", "generateButtonName", "isGenerate", "driverId", "officeId", "required"]))
            : _createCommentVNode("", true),
          ((isManagementOffice() || isSubManagementOffice()) && !isSelectedDriver())
            ? (_openBlock(), _createBlock(ToggleRow, {
                key: 4,
                title: _unref(i18n).global.t('pages.accounts.form.NoEmailAddress'),
                modelValue: account.value.no_email,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((account.value.no_email) = $event)),
                disabled: isDriverMode()
              }, null, 8, ["title", "modelValue", "disabled"]))
            : _createCommentVNode("", true),
          (account.value.no_email && !isSelectedDriver())
            ? (_openBlock(), _createBlock(AccountTextRow, {
                key: 5,
                title: _unref(i18n).global.t('pages.accounts.form.tempEmailAddress'),
                errors: errors.value['temporary_email'] ?? [],
                modelValue: account.value.temporary_email,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((account.value.temporary_email) = $event)),
                required: true
              }, null, 8, ["title", "errors", "modelValue"]))
            : _createCommentVNode("", true),
          (isDriverMode())
            ? (_openBlock(), _createBlock(PasswordForm, {
                key: 6,
                title: _unref(i18n).global.t('pages.accounts.form.password'),
                errors: errors.value['password'] ?? [],
                accountId: _unref(accountId),
                isDriverMode: isDriverMode(),
                driverId: account.value.driver_id,
                modelValue: account.value.password,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((account.value.password) = $event))
              }, null, 8, ["title", "errors", "accountId", "isDriverMode", "driverId", "modelValue"]))
            : _createCommentVNode("", true),
          ((isManagementOffice() || isSubManagementOffice()) && isSelectedDriver())
            ? (_openBlock(), _createBlock(ToggleRow, {
                key: 7,
                title: _unref(i18n).global.t('pages.accounts.form.operationVerificationAccount'),
                modelValue: account.value.enabled_factory_setting_account,
                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((account.value.enabled_factory_setting_account) = $event))
              }, null, 8, ["title", "modelValue"]))
            : _createCommentVNode("", true),
          (isSelectedManager())
            ? (_openBlock(), _createBlock(FormRow, {
                key: 8,
                title: _unref(i18n).global.t('pages.accounts.form.notificationSettings.title'),
                required: false
              }, {
                default: _withCtx(() => [
                  _createVNode(NotificationSettings, {
                    modelValue: notificationSettings.value,
                    temperatureAuthority: temperatureAuthority.value,
                    outputDataAuthority: outputDataAuthority.value
                  }, null, 8, ["modelValue", "temperatureAuthority", "outputDataAuthority"])
                ]),
                _: 1
              }, 8, ["title"]))
            : _createCommentVNode("", true),
          _createVNode(DepartmentColumn, {
            modelValue: selectedDepartment.value,
            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((selectedDepartment).value = $event)),
            selectedOfficeableType: selectedOfficeableType.value,
            selectedOffice: selectedOffice.value,
            initialItemId: initialDepartmentId.value,
            errors: errors.value['department_id'] ?? [],
            isDepartment: false
          }, null, 8, ["modelValue", "selectedOfficeableType", "selectedOffice", "initialItemId", "errors"]),
          _createVNode(AccountTextRow, {
            title: _unref(i18n).global.t('pages.accounts.telephoneNumber'),
            errors: errors.value['telephone_number'] ?? [],
            modelValue: account.value.telephone_number,
            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((account.value.telephone_number) = $event))
          }, null, 8, ["title", "errors", "modelValue"]),
          _createVNode(TextArea, {
            title: _unref(i18n).global.t('pages.accounts.form.comment'),
            errors: errors.value['comment'],
            modelValue: account.value.comment,
            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((account.value.comment) = $event))
          }, null, 8, ["title", "errors", "modelValue"])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_component_router_link, { to: { name: 'Accounts' } }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, { class: "ma-2 reset-btn" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(i18n).global.t('common.cancelBtn')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          (createAndUpdateAuthority.value)
            ? (_openBlock(), _createBlock(_component_v_btn, {
                key: 0,
                class: "ma-2 exec-btn",
                loading: isCreatingOrUpdating.value,
                onClick: _cache[14] || (_cache[14] = ($event: any) => (createOrUpdate()))
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(i18n).global.t('pages.common.button.conservation')), 1)
                ]),
                _: 1
              }, 8, ["loading"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _createVNode(InfoModal, {
        modelValue: modalState.value,
        "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((modalState).value = $event)),
        isDriverMode: isDriverMode(),
        accountId: _unref(accountId),
        modalContent: {
          email: account.value.email,
          password: account.value.password,
          name: [account.value.last_name, account.value.first_name]
        },
        isLoading: isCreatingOrUpdating.value,
        update: createOrUpdate,
        roleAssignAuthority: roleAssignAuthority.value
      }, null, 8, ["modelValue", "isDriverMode", "accountId", "modalContent", "isLoading", "roleAssignAuthority"])
    ])
  ]))
}
}

})